import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext"; 
import { useLocation } from "react-router-dom"; 
import { Loader2, Paperclip, X, RefreshCw } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" 
  ? "https://golden-shaina-maritimesupplysuites-ed826a4c.koyeb.app" 
  : (process.env.REACT_APP_API_URL || "https://maritime-supply-suite-backend.onrender.com");

export default function Chat() {
  const { socket, user } = useAuth(); 
  const location = useLocation(); 
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [adminOnline, setAdminOnline] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [previewFileName, setPreviewFileName] = useState("");
  const [filePreview, setFilePreview] = useState(null); 
  const [lightbox, setLightbox] = useState(null); 

  const fileRef = useRef();
  const bottomRef = useRef();
  const processedMessageIds = useRef(new Set());

  const renderMessageText = (msg) => {
    if (!msg || !msg.text) return null;

    const rawText = msg.text.toString();
    
    if (rawText.includes("AUTO_BOT_REPLY|")) {
      const cleanText = rawText.replace("AUTO_BOT_REPLY|", "").trim();
      return (
        <div style={styles.msgText}>
          <span style={{ fontWeight: "bold", color: "#93c5fd" }}>🤖 </span>
          {cleanText}
        </div>
      );
    }

    return <div style={styles.msgText}>{rawText}</div>;
  };

  const getFullImageUrl = (path) => {
    if (!path) return "";
    if (path.includes("res.cloudinary.com") || path.startsWith("http")) return path;
    return `${API_BASE}/${path.replace(/\\/g, "/").replace(/^\/+/, "")}`;
  };

  const scrollBottom = useCallback((behavior = "smooth") => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior });
  }, []);

  const triggerSeen = useCallback(async () => {
    if (!user?._id) return;
    try { await api.post(`/api/chat/seen/${user._id}`); } catch (err) { console.warn("Sync deferred."); }
  }, [user?._id]);

  const loadHistory = useCallback(async () => {
    if (!user?._id) return;
    setIsRefreshing(true);
    try {
      const res = await api.get("/api/chat/history");
      const history = res.data || [];
      processedMessageIds.current = new Set(history.map(m => m._id));
      setMessages(history.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
      setTimeout(() => scrollBottom("auto"), 100);
    } catch (err) { 
      console.error("History sync error:", err); 
    } finally { 
      setIsRefreshing(false); 
    }
  }, [user?._id, scrollBottom]);

  useEffect(() => {
    loadHistory();
    if (location.state?.initialMessage) {
      setText(location.state.initialMessage);
      window.history.replaceState({}, document.title);
    }
  }, [loadHistory, location.state]);

  useEffect(() => {
    if (!socket || !user?._id) return;
    const roomId = String(user._id);

    const onConnect = () => {
      socket.emit("join", roomId);
      socket.emit("checkAdminStatus");
    };

    if (socket.connected) onConnect();
    socket.on("connect", onConnect);

    const handleNewMessage = (msg) => {
      if (!msg || !msg._id) return;
      const targetId = msg.userId || msg.user;
      if (String(targetId) !== roomId) return;
      if (processedMessageIds.current.has(msg._id)) return;
      
      processedMessageIds.current.add(msg._id);
      setMessages((prev) => {
        const filtered = prev.filter(m => !m.isOptimistic || m.text !== msg.text);
        return [...filtered, msg].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      setTimeout(() => scrollBottom("smooth"), 100);
      if (msg.senderRole === "admin") triggerSeen();
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("admin:status", (status) => setAdminOnline(!!status));

    return () => {
      socket.off("connect", onConnect);
      socket.off("newMessage", handleNewMessage);
      socket.off("admin:status");
    };
  }, [socket, user?._id, scrollBottom, triggerSeen]);

  const sendMessage = async () => {
    const trimmedText = text.trim();
    const hasFile = fileRef.current?.files?.[0];
    if (!user?._id || (!trimmedText && !hasFile) || isSending) return;

    setIsSending(true);
    const tempId = `temp-${Date.now()}`;
    const originalText = text;
    const fileToUpload = hasFile;

    const optimisticMsg = {
      _id: tempId, text: originalText, senderRole: "user", userId: user._id,
      createdAt: new Date().toISOString(), file: filePreview, isOptimistic: true 
    };

    setMessages(prev => [...prev, optimisticMsg]);
    setText(""); setPreviewFileName(""); setFilePreview(null);
    scrollBottom("smooth");

    const formData = new FormData();
    formData.append("userId", user._id);
    formData.append("text", originalText);
    formData.append("senderRole", "user");
    if (fileToUpload) formData.append("file", fileToUpload);

    try {
      const res = await api.post("/api/chat/send", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      processedMessageIds.current.add(res.data._id);
      setMessages(prev => prev.map(m => m._id === tempId ? res.data : m));
    } catch (err) {
      setMessages(prev => prev.filter(m => m._id !== tempId));
      setText(originalText);
    } finally {
      setIsSending(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.headerTitle}>Support Systems</div>
          <div style={styles.headerSub}>NODE: {user?.node_id || "ACTIVE"}</div>
        </div>
        <div style={styles.headerActions}>
          <button onClick={loadHistory} disabled={isRefreshing} style={styles.refreshBtn}>
            <RefreshCw size={14} className={isRefreshing ? "spin" : ""} />
          </button>
          <div style={styles.statusRow}>
            <div style={styles.statusDot(adminOnline)} />
            <span style={styles.statusLabel}>{adminOnline ? "ONLINE" : "STANDBY"}</span>
          </div>
        </div>
      </div>

      <div style={styles.messageList}>
        {messages.map((m) => {
          const isAdmin = m.senderRole === "admin";
          return (
            <div key={m._id} style={isAdmin ? styles.rowAdmin : styles.rowUser(m.isOptimistic)}>
              <div style={isAdmin ? styles.bubbleAdmin : styles.bubbleUser}>
                {m.header && <div style={styles.msgHeader}>{m.header}</div>}
                {renderMessageText(m)}
                {m.file && (
  <div style={styles.fileContainer}>
    {/* If it's the known broken asset, don't even render the img tag */}
    {m.file.includes("ASSET-1776075339077-47618") ? null : (
      <img 
        src={getFullImageUrl(m.file)} 
        alt="attachment" 
        style={styles.chatImg}
        onClick={() => setLightbox(getFullImageUrl(m.file))}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    )}
  </div>
)}
              </div>
              <div style={styles.metaRow(isAdmin)}>
                {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {!isAdmin && !m.isOptimistic && (
                  <span style={{ color: m.seen ? "#60a5fa" : "#64748b" }}>{m.seen ? " ✓✓" : " ✓"}</span>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div style={styles.inputArea}>
        {previewFileName && (
          <div style={styles.previewContainer}>
            <span style={styles.previewText}>{previewFileName}</span>
            <X size={14} onClick={() => { setPreviewFileName(""); setFilePreview(null); }} style={{cursor:'pointer'}}/>
          </div>
        )}
        <div style={styles.inputFlex}>
          <label style={styles.attachBtn}>
            <Paperclip size={18} />
            <input type="file" ref={fileRef} hidden onChange={(e) => {
              const f = e.target.files[0];
              if(f) {
                setPreviewFileName(f.name);
                if(f.type.startsWith("image/")) setFilePreview(URL.createObjectURL(f));
              }
            }} />
          </label>
          <input 
            value={text} 
            onChange={(e) => setText(e.target.value)} 
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder="Type message..." 
            disabled={isSending}
            style={styles.input}
          />
          <button 
            onClick={sendMessage} 
            disabled={isSending || (!text.trim() && !previewFileName)}
            style={styles.sendBtn(isSending || (!text.trim() && !previewFileName))}
          >
            {isSending ? <Loader2 size={18} className="spin" /> : "Send"}
          </button>
        </div>
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={styles.lightboxOverlay}>
          <img src={lightbox} alt="Full" style={styles.lightboxImg} />
        </div>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  container: { display: "flex", flexDirection: "column", height: "100%", background: "#0f172a", borderRadius: "12px", border: "1px solid #334155", overflow: "hidden" },
  header: { background: "#1e293b", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #334155" },
  headerTitle: { color: "white", fontSize: "14px", fontWeight: "bold" },
  headerSub: { color: "#60a5fa", fontSize: "10px", fontWeight: "bold" },
  headerActions: { display: "flex", alignItems: "center", gap: "10px" },
  refreshBtn: { background: "none", border: "none", color: "white", cursor: "pointer" },
  statusRow: { display: "flex", alignItems: "center", gap: "6px" },
  statusDot: (on) => ({ width: "6px", height: "6px", borderRadius: "50%", background: on ? "#4ade80" : "#64748b" }),
  statusLabel: { color: "white", fontSize: "10px" },
  messageList: { flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "12px" },
  rowAdmin: { alignSelf: "flex-start", maxWidth: "80%" },
  rowUser: (opt) => ({ alignSelf: "flex-end", maxWidth: "80%", opacity: opt ? 0.6 : 1 }),
  bubbleAdmin: { background: "#1e293b", padding: "10px 14px", borderRadius: "12px 12px 12px 2px", border: "1px solid #334155" },
  bubbleUser: { background: "#2563eb", padding: "10px 14px", borderRadius: "12px 12px 2px 12px" },
  msgHeader: { color: "#93c5fd", fontSize: "10px", fontWeight: "bold", marginBottom: "4px", textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.1)" },
  msgText: { color: "white", fontSize: "13px", lineHeight: "1.4", wordBreak: "break-word" },
  fileContainer: { marginTop: "8px" },
  chatImg: { maxWidth: "100%", borderRadius: "4px", cursor: "pointer" },
  metaRow: (isAdmin) => ({ fontSize: "9px", color: "#94a3b8", marginTop: "4px", textAlign: isAdmin ? "left" : "right" }),
  inputArea: { padding: "12px", background: "#1e293b", borderTop: "1px solid #334155" },
  inputFlex: { display: "flex", alignItems: "center", gap: "8px" },
  attachBtn: { color: "#94a3b8", cursor: "pointer" },
  input: { flex: 1, background: "#0f172a", border: "1px solid #334155", borderRadius: "20px", padding: "8px 16px", color: "white", outline: "none", fontSize: "13px" },
  sendBtn: (disabled) => ({ background: disabled ? "#64748b" : "#2563eb", border: "none", borderRadius: "8px", padding: "6px 12px", color: "white", cursor: disabled ? "default" : "pointer", fontSize: "13px" }),
  previewContainer: { display: "flex", background: "#0f172a", padding: "4px 10px", borderRadius: "4px", marginBottom: "8px", justifyContent: "space-between", color: "#94a3b8", fontSize: "11px" },
  lightboxOverlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.9)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" },
  lightboxImg: { maxWidth: "90%", maxHeight: "90%" }
};
